import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, getUserId } from "../lib/api";
import { Plus, ArrowRight } from "lucide-react";

const GOAL_LABELS = {
  full_audit: "Full Audit",
  grow_revenue: "Grow Revenue",
  cut_waste: "Cut Waste",
  understand_history: "Understand History",
};

export default function SavedBriefingsPage() {
  const userId = getUserId();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const { items } = await api.listBriefings(userId);
        setItems(items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, navigate]);

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-12 md:px-10 md:py-16">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">saved</p>
            <h1 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              Briefings
            </h1>
            <p className="mt-2 text-sm text-slate-600">Snapshots you've generated are stored here.</p>
          </div>
          <Link
            to="/new"
            className="inline-flex items-center gap-2 rounded-md bg-[#0052FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0040CC]"
            data-testid="new-briefing-link"
          >
            <Plus size={14} strokeWidth={1.8} /> New briefing
          </Link>
        </div>

        {loading ? (
          <div className="mt-10 space-y-3">
            <div className="shimmer h-20 w-full rounded-md" />
            <div className="shimmer h-20 w-full rounded-md" />
          </div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-medium text-slate-900">No briefings yet.</p>
            <p className="mt-2 text-sm text-slate-500">Generate your first one — it'll land here.</p>
          </div>
        ) : (
          <ul className="mt-8 space-y-3">
            {items.map((b) => (
              <li key={b.briefing_id}>
                <Link
                  to={`/briefings/${b.briefing_id}`}
                  className="card-soft flex items-center justify-between gap-4 p-5 transition-all"
                  data-testid={`briefing-row-${b.briefing_id}`}
                >
                  <div>
                    <p className="font-heading text-base font-medium text-slate-900">
                      {b.customer_name || b.customer_id}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Last {b.lookback_days} days · {GOAL_LABELS[b.goal] || b.goal} ·{" "}
                      {new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.8} className="text-slate-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
